import { Injectable } from '@angular/core';
import { EventId } from '@interfaces';
import { FlowTypes, urlMapper } from '@interfaces/models';
import { select, Store } from '@ngrx/store';
import { LoggerCodes } from '@shared/modules/logger/logger';
import {
  flowTypeSelection,
  Go,
  LogError,
  LogInfo,
  selectAccountType,
  selectIsProspect,
  TrackProcessError,
  UpdateDisplayedAccounts,
} from '@store/index';
import * as fromRootStore from '@store/index';
import { selectClientAccountsLoaded } from '@store/selectors/client-accounts/client-accounts.selectors';
import { selectInheritanceEligibility } from '@store/selectors/eligibility/inheritance/inheritance-eligibility.selectors';
import { CookieService } from 'ngx-cookie-service';
import { catchError, map, Observable, of, switchMap, take, tap, withLatestFrom } from 'rxjs';

@Injectable()
export class InheritanceEligibilityGuard {
  constructor(
    private globalStore: Store<fromRootStore.State>,
    private cookieService: CookieService,
  ) {}
  canActivate(): Observable<boolean> {
    if (this.cookieService.get('routeGuard') === 'OFF') {
      return of(true);
    }
    return this.globalStore.pipe(
      select(selectIsProspect),
      take(1),
      switchMap((isProspect) => {
        // Prospects don't have accounts, skip eligibility check
        if (isProspect) {
          return of(true);
        }

        // For clients, check eligibility
        // ClientDataGuard already loaded accounts via LoadAccountRegistrations()
        return this.globalStore.pipe(
          select(selectInheritanceEligibility),
          withLatestFrom(
            this.globalStore.pipe(select(selectAccountType)),
            this.globalStore.pipe(select(selectClientAccountsLoaded)),
            this.globalStore.pipe(select(flowTypeSelection)),
          ),
          take(1), // Get current values immediately
          tap(
            ([
              { isEligible, isJointAccount, eligibleJointAccounts },
              accountFromType,
              ,
              flowType,
            ]) => {
              if (isEligible) {
                const eligibleLogMessage =
                  flowType === FlowTypes.MULTI_INHERITANCE
                    ? 'Eligible multi inheritance without selected account type'
                    : `Eligible inheritance ${accountFromType} account`;
                this.globalStore.dispatch(
                  LogInfo({
                    payload: {
                      eventId: 'eligible-accounts-found-inheritanceEligibilityGuard',
                      message: eligibleLogMessage,
                      code: LoggerCodes.GENERAL_SUCCESS,
                    },
                  }),
                );
                if (isJointAccount) {
                  this.globalStore.dispatch(
                    UpdateDisplayedAccounts({ payload: eligibleJointAccounts }),
                  );
                }
              } else {
                this.globalStore.dispatch(
                  LogInfo({
                    payload: {
                      eventId: EventId.INELIGIBLE_PAGE_ROUTE,
                      message: `No eligible inheritance ${accountFromType} FROM account`,
                      ineligibleFromAccountType: accountFromType,
                      flowType: FlowTypes.INHERITANCE,
                    },
                  }),
                );
                this.globalStore.dispatch(
                  Go({ path: [`${urlMapper[FlowTypes.INHERITANCE]}/ineligible`] }),
                );
              }
            },
          ),
          map(([{ isEligible }]) => isEligible),
          catchError((err) => {
            this.globalStore.dispatch(
              TrackProcessError({
                errorCode: `coo-ineligible-app_error`,
              }),
            );
            this.globalStore.dispatch(
              LogError({
                payload: {
                  eventId: 'inheritanceEligibilityGuard-unknown-error',
                  message: `Inheritance eligbility guard failed due to unknown error. ${err.message}`,
                  code: LoggerCodes.NULL_FAILURE,
                },
              }),
            );

            this.globalStore.dispatch(Go({ path: ['error'] }));

            return of(false);
          }),
        );
      }),
    );
  }
}
